import { type, lensPath, set } from 'ramda';
import React, { createContext, useState, useEffect, useContext } from 'react';
import { NativeSelect, Modal, ModalTitle, ModalSubtitle, ModalBody, TextField, Button, ModalActionsContainer, useModal, NumberField, RadioGroup, ToggleSwitch } from '@erso/common-ui-react';
import { FaTimes, FaPlus } from 'react-icons/fa';
var EXPRESSION_TYPE_AND = 'And';
var EXPRESSION_TYPE_OR = 'Or';
var EXPRESSION_TYPE_NOT = 'Not';
var EXPRESSION_TYPE_EQUALS = 'Equals';
var EXPRESSION_TYPE_GREATER_THAN = 'GreaterThan';
var EXPRESSION_TYPE_GREATER_THAN_EQUALS = 'GreaterThanEquals';
var EXPRESSION_TYPE_LESS_THAN = 'LessThan';
var EXPRESSION_TYPE_LESS_THAN_EQUALS = 'LessThanEquals';
var EXPRESSION_TYPE_INCLUDES = 'Includes';
var EXPRESSION_TYPE_WITHIN = 'Within';
var EXPRESSION_TYPE_SUBSET = 'Subset';
var VALUE_TYPE_TEXT = 'Text';
var VALUE_TYPE_TEXT_LIST = 'TextList';
var VALUE_TYPE_NUMBER = 'Number';
var VALUE_TYPE_VERSION = 'Version';

var makeParam = function makeParam(param) {
  return {
    param: param
  };
};

var makeValueText = function makeValueText(string) {
  return {
    _type: VALUE_TYPE_TEXT,
    value: string
  };
};

var makeValueTextList = function makeValueTextList(strings) {
  return {
    _type: VALUE_TYPE_TEXT_LIST,
    values: strings
  };
};

var makeValueNumber = function makeValueNumber(integer) {
  return {
    _type: VALUE_TYPE_NUMBER,
    value: integer
  };
};

var makeValueVersion = function makeValueVersion(string) {
  return {
    _type: VALUE_TYPE_VERSION,
    value: string
  };
};

var makeAnd = function makeAnd(leftExpr, rightExpr) {
  return {
    _type: EXPRESSION_TYPE_AND,
    leftExpr: leftExpr,
    rightExpr: rightExpr
  };
};

var makeOr = function makeOr(leftExpr, rightExpr) {
  return {
    _type: EXPRESSION_TYPE_OR,
    leftExpr: leftExpr,
    rightExpr: rightExpr
  };
};

var makeNot = function makeNot(expr) {
  return {
    _type: EXPRESSION_TYPE_NOT,
    expr: expr
  };
};

var makeEquals = function makeEquals(param, rightSide) {
  return {
    _type: EXPRESSION_TYPE_EQUALS,
    leftSide: makeParam(param),
    rightSide: rightSide
  };
};

var makeNotEquals = function makeNotEquals(param, rightSide) {
  return makeNot(makeEquals(param, rightSide));
};

var makeEqualsText = function makeEqualsText(param, string) {
  return makeEquals(param, makeValueText(string));
};

var makeEqualsTextList = function makeEqualsTextList(param, strings) {
  return makeEquals(param, makeValueTextList(strings));
};

var makeEqualsNumber = function makeEqualsNumber(param, integer) {
  return makeEquals(param, makeValueNumber(integer));
};

var makeEqualsVersion = function makeEqualsVersion(param, string) {
  return makeEquals(param, makeValueVersion(string));
};

var makeNotEqualsText = function makeNotEqualsText(param, string) {
  return makeNot(makeEqualsText(param, string));
};

var makeNotEqualsTextList = function makeNotEqualsTextList(param, strings) {
  return makeNot(makeEqualsTextList(param, strings));
};

var makeNotEqualsNumber = function makeNotEqualsNumber(param, integer) {
  return makeNot(makeEqualsNumber(param, integer));
};

var makeNotEqualsVersion = function makeNotEqualsVersion(param, string) {
  return makeNot(makeEqualsVersion(param, string));
};

var makeNumberComparison = function makeNumberComparison(_type) {
  return function (param, integer) {
    return {
      _type: _type,
      leftSide: makeParam(param),
      rightSide: makeValueNumber(integer)
    };
  };
};

var makeGreaterThanNumber = makeNumberComparison(EXPRESSION_TYPE_GREATER_THAN);
var makeGreaterThanEqualsNumber = makeNumberComparison(EXPRESSION_TYPE_GREATER_THAN_EQUALS);
var makeLessThanNumber = makeNumberComparison(EXPRESSION_TYPE_LESS_THAN);
var makeLessThanEqualsNumber = makeNumberComparison(EXPRESSION_TYPE_LESS_THAN_EQUALS);

var makeVersionComparison = function makeVersionComparison(_type) {
  return function (param, string) {
    return {
      _type: _type,
      leftSide: makeParam(param),
      rightSide: makeValueVersion(string)
    };
  };
};

var makeGreaterThanVersion = makeVersionComparison(EXPRESSION_TYPE_GREATER_THAN);
var makeGreaterThanEqualsVersion = makeVersionComparison(EXPRESSION_TYPE_GREATER_THAN_EQUALS);
var makeLessThanVersion = makeVersionComparison(EXPRESSION_TYPE_LESS_THAN);
var makeLessThanEqualsVersion = makeVersionComparison(EXPRESSION_TYPE_LESS_THAN_EQUALS);

var makeIncludes = function makeIncludes(param, string) {
  return {
    _type: EXPRESSION_TYPE_INCLUDES,
    leftSide: makeParam(param),
    rightSide: string
  };
};

var makeNotIncludes = function makeNotIncludes(param, string) {
  return makeNot(makeIncludes(param, string));
};

var makeWithin = function makeWithin(param, strings) {
  return {
    _type: EXPRESSION_TYPE_WITHIN,
    leftSide: makeParam(param),
    rightSide: strings
  };
};

var makeNotWithin = function makeNotWithin(param, strings) {
  return makeNot(makeWithin(param, strings));
};

var makeSubset = function makeSubset(param, strings) {
  return {
    _type: EXPRESSION_TYPE_SUBSET,
    leftSide: makeParam(param),
    rightSide: strings
  };
};

var makeNotSubset = function makeNotSubset(param, strings) {
  return makeNot(makeSubset(param, strings));
};

var isNestedExpression = function isNestedExpression(e) {
  switch (e._type) {
    case EXPRESSION_TYPE_AND:
    case EXPRESSION_TYPE_OR:
      return true;

    default:
      return false;
  }
};

var isSameType = function isSameType(e1, e2) {
  return e1._type === e2._type;
};

var isType = function isType(e, type) {
  return e._type === type;
};

var isAnd = function isAnd(e) {
  return isType(e, EXPRESSION_TYPE_AND);
};

var isOr = function isOr(e) {
  return isType(e, EXPRESSION_TYPE_OR);
};

var isNot = function isNot(e) {
  return isType(e, EXPRESSION_TYPE_NOT);
};

var isEquals = function isEquals(e) {
  return isType(e, EXPRESSION_TYPE_EQUALS);
};

var isGreaterThan = function isGreaterThan(e) {
  return isType(e, EXPRESSION_TYPE_GREATER_THAN);
};

var isGreaterThanEquals = function isGreaterThanEquals(e) {
  return isType(e, EXPRESSION_TYPE_GREATER_THAN_EQUALS);
};

var isLessThan = function isLessThan(e) {
  return isType(e, EXPRESSION_TYPE_LESS_THAN);
};

var isLessThanEquals = function isLessThanEquals(e) {
  return isType(e, EXPRESSION_TYPE_LESS_THAN_EQUALS);
};

var isIncludes = function isIncludes(e) {
  return isType(e, EXPRESSION_TYPE_INCLUDES);
};

var isWithin = function isWithin(e) {
  return isType(e, EXPRESSION_TYPE_WITHIN);
};

var isSubset = function isSubset(e) {
  return isType(e, EXPRESSION_TYPE_SUBSET);
};

var isExpressionTypeValid = function isExpressionTypeValid(e) {
  if (!e) return false;

  switch (e._type) {
    case EXPRESSION_TYPE_AND:
    case EXPRESSION_TYPE_OR:
    case EXPRESSION_TYPE_NOT:
    case EXPRESSION_TYPE_EQUALS:
    case EXPRESSION_TYPE_GREATER_THAN:
    case EXPRESSION_TYPE_GREATER_THAN_EQUALS:
    case EXPRESSION_TYPE_LESS_THAN:
    case EXPRESSION_TYPE_LESS_THAN_EQUALS:
    case EXPRESSION_TYPE_INCLUDES:
    case EXPRESSION_TYPE_WITHIN:
    case EXPRESSION_TYPE_SUBSET:
      return true;

    default:
      return false;
  }
};

var isValueTypeValid = function isValueTypeValid(v) {
  if (!v) return false;

  switch (v._type) {
    case VALUE_TYPE_TEXT:
    case VALUE_TYPE_TEXT_LIST:
    case VALUE_TYPE_NUMBER:
    case VALUE_TYPE_VERSION:
      return true;

    default:
      return false;
  }
};

var isValueType = function isValueType(v, type) {
  return v._type === type;
};

var isTextValue = function isTextValue(v) {
  return isValueType(v, VALUE_TYPE_TEXT);
};

var isTextListValue = function isTextListValue(v) {
  return isValueType(v, VALUE_TYPE_TEXT_LIST);
};

var isNumberValue = function isNumberValue(v) {
  return isValueType(v, VALUE_TYPE_NUMBER);
};

var isVersionValue = function isVersionValue(v) {
  return isValueType(v, VALUE_TYPE_VERSION);
};

var expressionTreeUtils = {
  __proto__: null,
  EXPRESSION_TYPE_AND: EXPRESSION_TYPE_AND,
  EXPRESSION_TYPE_OR: EXPRESSION_TYPE_OR,
  EXPRESSION_TYPE_NOT: EXPRESSION_TYPE_NOT,
  EXPRESSION_TYPE_EQUALS: EXPRESSION_TYPE_EQUALS,
  EXPRESSION_TYPE_GREATER_THAN: EXPRESSION_TYPE_GREATER_THAN,
  EXPRESSION_TYPE_GREATER_THAN_EQUALS: EXPRESSION_TYPE_GREATER_THAN_EQUALS,
  EXPRESSION_TYPE_LESS_THAN: EXPRESSION_TYPE_LESS_THAN,
  EXPRESSION_TYPE_LESS_THAN_EQUALS: EXPRESSION_TYPE_LESS_THAN_EQUALS,
  EXPRESSION_TYPE_INCLUDES: EXPRESSION_TYPE_INCLUDES,
  EXPRESSION_TYPE_WITHIN: EXPRESSION_TYPE_WITHIN,
  EXPRESSION_TYPE_SUBSET: EXPRESSION_TYPE_SUBSET,
  VALUE_TYPE_TEXT: VALUE_TYPE_TEXT,
  VALUE_TYPE_TEXT_LIST: VALUE_TYPE_TEXT_LIST,
  VALUE_TYPE_NUMBER: VALUE_TYPE_NUMBER,
  VALUE_TYPE_VERSION: VALUE_TYPE_VERSION,
  makeAnd: makeAnd,
  makeOr: makeOr,
  makeNot: makeNot,
  makeEquals: makeEquals,
  makeNotEquals: makeNotEquals,
  makeEqualsText: makeEqualsText,
  makeEqualsTextList: makeEqualsTextList,
  makeEqualsNumber: makeEqualsNumber,
  makeEqualsVersion: makeEqualsVersion,
  makeNotEqualsText: makeNotEqualsText,
  makeNotEqualsTextList: makeNotEqualsTextList,
  makeNotEqualsNumber: makeNotEqualsNumber,
  makeNotEqualsVersion: makeNotEqualsVersion,
  makeGreaterThanNumber: makeGreaterThanNumber,
  makeGreaterThanEqualsNumber: makeGreaterThanEqualsNumber,
  makeLessThanNumber: makeLessThanNumber,
  makeLessThanEqualsNumber: makeLessThanEqualsNumber,
  makeGreaterThanVersion: makeGreaterThanVersion,
  makeGreaterThanEqualsVersion: makeGreaterThanEqualsVersion,
  makeLessThanVersion: makeLessThanVersion,
  makeLessThanEqualsVersion: makeLessThanEqualsVersion,
  makeIncludes: makeIncludes,
  makeNotIncludes: makeNotIncludes,
  makeWithin: makeWithin,
  makeNotWithin: makeNotWithin,
  makeSubset: makeSubset,
  makeNotSubset: makeNotSubset,
  isNestedExpression: isNestedExpression,
  isSameType: isSameType,
  isType: isType,
  isAnd: isAnd,
  isOr: isOr,
  isNot: isNot,
  isEquals: isEquals,
  isGreaterThan: isGreaterThan,
  isGreaterThanEquals: isGreaterThanEquals,
  isLessThan: isLessThan,
  isLessThanEquals: isLessThanEquals,
  isIncludes: isIncludes,
  isWithin: isWithin,
  isSubset: isSubset,
  isExpressionTypeValid: isExpressionTypeValid,
  isValueTypeValid: isValueTypeValid,
  isValueType: isValueType,
  isTextValue: isTextValue,
  isTextListValue: isTextListValue,
  isNumberValue: isNumberValue,
  isVersionValue: isVersionValue
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var toListForm = function toListForm(e) {
  return convertToLists(e);
};

var convertToLists = function convertToLists(e, childNodes) {
  if (childNodes === void 0) {
    childNodes = [];
  }

  var helper = function helper(innerExpr) {
    if (isSameType(e, innerExpr)) {
      convertToLists(innerExpr, childNodes);
    } else {
      childNodes.push(convertToLists(innerExpr));
    }
  };

  if (isNestedExpression(e)) {
    helper(e.leftExpr);
    helper(e.rightExpr);
    return {
      _type: e._type,
      childNodes: childNodes
    };
  }

  if (isNot(e)) {
    return _extends({}, convertToLists(e.expr), {
      not: true
    });
  }

  return e;
};

var validatePredicateListForm = function validatePredicateListForm(listForm) {
  var level0Node = listForm;
  if (!isAnd(level0Node)) return false;
  if (level0Node.not) return false;
  var level1Nodes = level0Node.childNodes;
  if (level1Nodes.some(function (node) {
    return !isEquals(node);
  })) return false;
  return true;
};

var wrapInAndNode = function wrapInAndNode(node) {
  return {
    _type: EXPRESSION_TYPE_AND,
    childNodes: [node]
  };
};

var toPredicateListForm = function toPredicateListForm(expression) {
  var listForm = toListForm(expression);

  if (!isAnd(listForm)) {
    listForm = wrapInAndNode(listForm);
  }

  if (!validatePredicateListForm(listForm)) {
    throw new Error('Expression cannot be represented in predicate list form');
  }

  return listForm;
};

var _excluded = ["not"];

var toTreeForm = function toTreeForm(root) {
  return handleExpression(root);
};

var handleExpression = function handleExpression(n) {
  switch (n._type) {
    case EXPRESSION_TYPE_AND:
    case EXPRESSION_TYPE_OR:
      return handleAndOr(n);

    case EXPRESSION_TYPE_EQUALS:
    case EXPRESSION_TYPE_GREATER_THAN:
    case EXPRESSION_TYPE_GREATER_THAN_EQUALS:
    case EXPRESSION_TYPE_LESS_THAN:
    case EXPRESSION_TYPE_LESS_THAN_EQUALS:
    case EXPRESSION_TYPE_INCLUDES:
    case EXPRESSION_TYPE_WITHIN:
    case EXPRESSION_TYPE_SUBSET:
      return handleLeaf(n);

    default:
      throw new Error("Unexpected Expression type: " + n._type);
  }
};

var handleAndOr = function handleAndOr(n) {
  if (n.childNodes === undefined) {
    return n;
  }

  if (n.childNodes.length === 0) {
    return n;
  }

  var result = n.childNodes.length === 1 ? handleExpression(n.childNodes[0]) : n.childNodes.reduce(function (e1, e2) {
    return {
      _type: n._type,
      leftExpr: handleExpression(e1),
      rightExpr: handleExpression(e2)
    };
  });
  return n.not ? makeNot(result) : result;
};

var handleLeaf = function handleLeaf(n) {
  var not = n.not,
      e = _objectWithoutPropertiesLoose(n, _excluded);

  return not ? makeNot(e) : e;
};

var normalise = function normalise(e) {
  return convertToLeftTree(unravelNestedNotNodes(e));
};

var unravelNestedNotNodes = function unravelNestedNotNodes(e, notCount) {
  if (notCount === void 0) {
    notCount = 0;
  }

  if (isNot(e)) {
    return unravelNestedNotNodes(e.expr, notCount + 1);
  }

  if (notCount % 2 === 0) {
    switch (e._type) {
      case EXPRESSION_TYPE_AND:
      case EXPRESSION_TYPE_OR:
        return _extends({}, e, {
          leftExpr: unravelNestedNotNodes(e.leftExpr),
          rightExpr: unravelNestedNotNodes(e.rightExpr)
        });

      default:
        return e;
    }
  }

  switch (e._type) {
    case EXPRESSION_TYPE_AND:
    case EXPRESSION_TYPE_OR:
      return makeNot(_extends({}, e, {
        leftExpr: unravelNestedNotNodes(e.leftExpr),
        rightExpr: unravelNestedNotNodes(e.rightExpr)
      }));

    case EXPRESSION_TYPE_GREATER_THAN:
      return _extends({}, e, {
        _type: EXPRESSION_TYPE_LESS_THAN_EQUALS
      });

    case EXPRESSION_TYPE_GREATER_THAN_EQUALS:
      return _extends({}, e, {
        _type: EXPRESSION_TYPE_LESS_THAN
      });

    case EXPRESSION_TYPE_LESS_THAN:
      return _extends({}, e, {
        _type: EXPRESSION_TYPE_GREATER_THAN_EQUALS
      });

    case EXPRESSION_TYPE_LESS_THAN_EQUALS:
      return _extends({}, e, {
        _type: EXPRESSION_TYPE_GREATER_THAN
      });

    default:
      return makeNot(e);
  }
};

var convertToLeftTree = function convertToLeftTree(e, nodes) {
  if (nodes === void 0) {
    nodes = [];
  }

  var helper = function helper(innerExpr) {
    if (isSameType(e, innerExpr)) {
      convertToLeftTree(innerExpr, nodes);
    } else {
      nodes.push(convertToLeftTree(innerExpr));
    }
  };

  if (isNestedExpression(e)) {
    helper(e.leftExpr);
    helper(e.rightExpr);
    var makeNode = getNestedExpressionNodeMaker(e);
    return reassembleLeftTree(makeNode, nodes);
  }

  if (isNot(e)) {
    return _extends({}, e, {
      expr: convertToLeftTree(e.expr)
    });
  }

  return e;
};

var reassembleLeftTree = function reassembleLeftTree(makeNode, nodes) {
  var head = nodes[0],
      tail = nodes.slice(1);
  return tail.reduce(makeNode, head);
};

var getNestedExpressionNodeMaker = function getNestedExpressionNodeMaker(e) {
  switch (e._type) {
    case EXPRESSION_TYPE_AND:
      return makeAnd;

    case EXPRESSION_TYPE_OR:
      return makeOr;

    default:
      throw new Error("Unexpected nested expression type: " + e._type);
  }
};

var deepEquals = function deepEquals(e1, e2) {
  if (!isExpressionTypeValid(e1)) {
    throw new Error('Invalid left expression type');
  }

  if (!isExpressionTypeValid(e2)) {
    throw new Error('Invalid right expression type');
  }

  if (!isSameType(e1, e2)) return false;

  switch (e1._type) {
    case EXPRESSION_TYPE_AND:
    case EXPRESSION_TYPE_OR:
      return deepEquals(e1.leftExpr, e2.leftExpr) && deepEquals(e1.rightExpr, e2.rightExpr);

    case EXPRESSION_TYPE_NOT:
      return deepEquals(e1.expr, e2.expr);

    case EXPRESSION_TYPE_EQUALS:
    case EXPRESSION_TYPE_GREATER_THAN:
    case EXPRESSION_TYPE_GREATER_THAN_EQUALS:
    case EXPRESSION_TYPE_LESS_THAN:
    case EXPRESSION_TYPE_LESS_THAN_EQUALS:
      return leftSideParamEquals(e1.leftSide, e2.leftSide) && rightSideValueEquals(e1.rightSide, e2.rightSide);

    case EXPRESSION_TYPE_INCLUDES:
      return leftSideParamEquals(e1.leftSide, e2.leftSide) && rightSideTextEquals(e1.rightSide, e2.rightSide);

    case EXPRESSION_TYPE_WITHIN:
      return leftSideParamEquals(e1.leftSide, e2.leftSide) && rightSideTextListEquals(e1.rightSide, e2.rightSide);

    case EXPRESSION_TYPE_SUBSET:
      return leftSideParamEquals(e1.leftSide, e2.leftSide) && rightSideTextListEquals(e1.rightSide, e2.rightSide);

    default:
      throw new Error("Unexpected expression type \"" + e1._type + "\"");
  }
};

var leftSideParamEquals = function leftSideParamEquals(leftSide1, leftSide2) {
  if (type(leftSide1) !== 'Object' || type(leftSide1.param) !== 'String') {
    throw new Error('Expected left Param to contain a param name');
  }

  if (type(leftSide2) !== 'Object' || type(leftSide2.param) !== 'String') {
    throw new Error('Expected right Param to contain a param name');
  }

  return leftSide1.param === leftSide2.param;
};

var rightSideValueEquals = function rightSideValueEquals(value1, value2) {
  if (!isValueTypeValid(value1)) {
    throw new Error('Invalid left value type');
  }

  if (!isValueTypeValid(value2)) {
    throw new Error('Invalid right value type');
  }

  if (value1._type !== value2._type) return false;

  switch (value1._type) {
    case VALUE_TYPE_TEXT:
      return rightSideTextEquals(value1.value, value2.value);

    case VALUE_TYPE_TEXT_LIST:
      return rightSideTextListEquals(value1.values, value2.values);

    case VALUE_TYPE_NUMBER:
      return rightSideNumberEquals(value1.value, value2.value);

    case VALUE_TYPE_VERSION:
      return rightSideVersionEquals(value1.value, value2.value);

    default:
      throw new Error("Unexpected value type \"" + value1._type + "\"");
  }
};

var rightSideTextEquals = function rightSideTextEquals(text1, text2) {
  if (type(text1) !== 'String') {
    throw new Error("Expected left Text value to be a string");
  }

  if (type(text2) !== 'String') {
    throw new Error("Expected right Text value to be a string");
  }

  return text1 === text2;
};

var rightSideTextListEquals = function rightSideTextListEquals(textList1, textList2) {
  if (type(textList1) !== 'Array') {
    throw new Error("Expected left TextList value to be an array");
  }

  if (textList1.some(function (value) {
    return type(value) !== 'String';
  })) {
    throw new Error("Expected left TextList value to be an array of strings");
  }

  if (type(textList2) !== 'String') {
    throw new Error("Expected right TextList value to be an array");
  }

  if (textList2.some(function (value) {
    return type(value) !== 'String';
  })) {
    throw new Error("Expected right TextList value to be an array of strings");
  }

  return textList1.length === textList2.length && textList1.every(function (value, index) {
    return value === textList2[index];
  });
};

var rightSideNumberEquals = function rightSideNumberEquals(number1, number2) {
  if (!(Number.isInteger(number1) || number1 === '')) {
    throw new Error("Expected left Number value to be an integer (or blank string)");
  }

  if (!(Number.isInteger(number2) || number2 === '')) {
    throw new Error("Expected right Number value to be an integer (or blank string)");
  }

  return number1 === number2;
};

var rightSideVersionEquals = function rightSideVersionEquals(version1, version2) {
  if (type(version1) !== 'String') {
    throw new Error("Expected left Version value to be a string");
  }

  if (type(version2) !== 'String') {
    throw new Error("Expected right Version value to be a string");
  }

  return version1 === version2;
};

var ParamsContext = createContext();
var NOT_MODE = true;

var prettyPrint = function prettyPrint(e) {
  var acc = '';

  var append = function append(s) {
    acc += s;
  };

  evaluateExpression(append, e);
  return acc;
};

var evaluateExpression = function evaluateExpression(fn, e) {
  switch (e._type) {
    case EXPRESSION_TYPE_AND:
      return evaluateAnd(fn, e);

    case EXPRESSION_TYPE_OR:
      return evaluateOr(fn, e);

    case EXPRESSION_TYPE_NOT:
      return evaluateNot(fn, e);

    case EXPRESSION_TYPE_EQUALS:
      return evaluateEquals(fn, e);

    case EXPRESSION_TYPE_GREATER_THAN:
      return evaluateComparison(fn, e, '>');

    case EXPRESSION_TYPE_GREATER_THAN_EQUALS:
      return evaluateComparison(fn, e, '>=');

    case EXPRESSION_TYPE_LESS_THAN:
      return evaluateComparison(fn, e, '<');

    case EXPRESSION_TYPE_LESS_THAN_EQUALS:
      return evaluateComparison(fn, e, '<=');

    case EXPRESSION_TYPE_INCLUDES:
      return evaluateIncludes(fn, e);

    case EXPRESSION_TYPE_WITHIN:
      return evaluateWithin(fn, e);

    case EXPRESSION_TYPE_SUBSET:
      return evaluateSubset(fn, e);

    default:
      throw new Error("Unexpected Expression type: " + e._type);
  }
};

var maybeWrapInParens = function maybeWrapInParens(fn, e, p) {
  var needParens = isNestedExpression(e) && (!p || !isSameType(p, e));

  if (needParens) {
    fn('(');
  }

  evaluateExpression(fn, e);

  if (needParens) {
    fn(')');
  }
};

var evaluateAnd = function evaluateAnd(fn, e) {
  maybeWrapInParens(fn, e.leftExpr, e);
  fn(' and ');
  maybeWrapInParens(fn, e.rightExpr, e);
};

var evaluateOr = function evaluateOr(fn, e) {
  maybeWrapInParens(fn, e.leftExpr, e);
  fn(' or ');
  maybeWrapInParens(fn, e.rightExpr, e);
};

var evaluateNot = function evaluateNot(fn, e) {
  switch (e.expr._type) {
    case EXPRESSION_TYPE_EQUALS:
      return evaluateEquals(fn, e.expr, NOT_MODE);

    case EXPRESSION_TYPE_GREATER_THAN:
      return evaluateComparison(fn, e.expr, '>', NOT_MODE);

    case EXPRESSION_TYPE_GREATER_THAN_EQUALS:
      return evaluateComparison(fn, e.expr, '>=', NOT_MODE);

    case EXPRESSION_TYPE_LESS_THAN:
      return evaluateComparison(fn, e.expr, '<', NOT_MODE);

    case EXPRESSION_TYPE_LESS_THAN_EQUALS:
      return evaluateComparison(fn, e.expr, '<=', NOT_MODE);

    case EXPRESSION_TYPE_INCLUDES:
      return evaluateIncludes(fn, e.expr, NOT_MODE);

    case EXPRESSION_TYPE_WITHIN:
      return evaluateWithin(fn, e.expr, NOT_MODE);

    case EXPRESSION_TYPE_SUBSET:
      return evaluateSubset(fn, e.expr, NOT_MODE);

    default:
      fn('not ');
      maybeWrapInParens(fn, e.expr);
      break;
  }
};

var evaluateEquals = function evaluateEquals(fn, e, notMode) {
  evaluateParam(fn, e.leftSide);
  fn(notMode ? ' != ' : ' = ');
  evaluateValue(fn, e.rightSide);
};

var evaluateComparison = function evaluateComparison(fn, e, op, notMode) {
  evaluateParam(fn, e.leftSide);
  fn(notMode ? " not " + op + " " : " " + op + " ");
  evaluateValue(fn, e.rightSide);
};

var evaluateIncludes = function evaluateIncludes(fn, e, notMode) {
  evaluateParam(fn, e.leftSide);
  fn(notMode ? ' not includes ' : ' includes ');
  evaluateText(fn, e.rightSide);
};

var evaluateWithin = function evaluateWithin(fn, e, notMode) {
  evaluateParam(fn, e.leftSide);
  fn(notMode ? ' not within ' : ' within ');
  evaluateTextList(fn, e.rightSide);
};

var evaluateSubset = function evaluateSubset(fn, e, notMode) {
  evaluateParam(fn, e.leftSide);
  fn(notMode ? ' not subset ' : ' subset ');
  evaluateTextList(fn, e.rightSide);
};

var evaluateParam = function evaluateParam(fn, p) {
  fn(p.param ? p.param : '?');
};

var evaluateValue = function evaluateValue(fn, v) {
  switch (v._type) {
    case VALUE_TYPE_TEXT:
      return evaluateText(fn, v.value);

    case VALUE_TYPE_TEXT_LIST:
      return evaluateTextList(fn, v.values);

    case VALUE_TYPE_NUMBER:
      return evaluateNumber(fn, v.value);

    case VALUE_TYPE_VERSION:
      return evaluateVersion(fn, v.value);

    default:
      throw new Error("Unexpected Value type: " + v._type);
  }
};

var evaluateText = function evaluateText(fn, s) {
  fn(addQuotes(s));
};

var evaluateTextList = function evaluateTextList(fn, ss) {
  var separator = ', ';
  fn("[" + ss.map(addQuotes).join(separator) + "]");
};

var evaluateNumber = function evaluateNumber(fn, i) {
  Number.isInteger(i) ? fn("" + i) : fn('?');
};

var evaluateVersion = function evaluateVersion(fn, s) {
  fn(s.trim() ? addQuotes(s) : '?');
};

var addQuotes = function addQuotes(s) {
  return "\"" + s + "\"";
};

var styles = {
  "panel": "_2HrnX"
};

var PrettyPrintPanel = function PrettyPrintPanel(_ref) {
  var treeForm = _ref.treeForm;

  var _useState = useState(''),
      prettyPrinted = _useState[0],
      setPrettyPrinted = _useState[1];

  useEffect(function () {
    try {
      setPrettyPrinted(prettyPrint(treeForm));
    } catch (_unused) {
      setPrettyPrinted('');
    }
  }, [treeForm]);
  return /*#__PURE__*/React.createElement("div", {
    className: styles.panel
  }, /*#__PURE__*/React.createElement("div", null, prettyPrinted));
};

var joinStrings = function joinStrings(strings) {
  return Array.isArray(strings) ? strings.join(', ') : String(strings);
};

var unwrapValueAsString = function unwrapValueAsString(condition) {
  switch (condition._type) {
    case EXPRESSION_TYPE_EQUALS:
    case EXPRESSION_TYPE_GREATER_THAN:
    case EXPRESSION_TYPE_GREATER_THAN_EQUALS:
    case EXPRESSION_TYPE_LESS_THAN:
    case EXPRESSION_TYPE_LESS_THAN_EQUALS:
      switch (condition.rightSide._type) {
        case VALUE_TYPE_TEXT:
          return condition.rightSide.value;

        case VALUE_TYPE_TEXT_LIST:
          return joinStrings(condition.rightSide.values);

        case VALUE_TYPE_NUMBER:
          return String(condition.rightSide.value);

        case VALUE_TYPE_VERSION:
          return condition.rightSide.value;

        default:
          throw new Error("Unexpected rightSide type \"" + condition.rightSide._type + "\"");
      }

    case EXPRESSION_TYPE_INCLUDES:
      return condition.rightSide;

    case EXPRESSION_TYPE_WITHIN:
    case EXPRESSION_TYPE_SUBSET:
      return joinStrings(condition.rightSide);

    default:
      throw new Error("Unexpected operator \"" + condition._type + "\"");
  }
};

var unwrapTextList = function unwrapTextList(condition) {
  switch (condition._type) {
    case EXPRESSION_TYPE_EQUALS:
    case EXPRESSION_TYPE_GREATER_THAN:
    case EXPRESSION_TYPE_GREATER_THAN_EQUALS:
    case EXPRESSION_TYPE_LESS_THAN:
    case EXPRESSION_TYPE_LESS_THAN_EQUALS:
      switch (condition.rightSide._type) {
        case VALUE_TYPE_TEXT_LIST:
          return condition.rightSide.values;

        default:
          return [];
      }

    case EXPRESSION_TYPE_WITHIN:
    case EXPRESSION_TYPE_SUBSET:
      return condition.rightSide;

    default:
      return [];
  }
};

var unwrapValue = function unwrapValue(operator, rightSide) {
  switch (operator) {
    case EXPRESSION_TYPE_EQUALS:
    case EXPRESSION_TYPE_GREATER_THAN:
    case EXPRESSION_TYPE_GREATER_THAN_EQUALS:
    case EXPRESSION_TYPE_LESS_THAN:
    case EXPRESSION_TYPE_LESS_THAN_EQUALS:
      switch (rightSide._type) {
        case VALUE_TYPE_TEXT:
          return rightSide.value;

        case VALUE_TYPE_TEXT_LIST:
          return rightSide.values;

        case VALUE_TYPE_NUMBER:
          return rightSide.value;

        case VALUE_TYPE_VERSION:
          return rightSide.value;

        default:
          throw new Error("Unexpected rightSide type \"" + rightSide._type + "\"");
      }

    case EXPRESSION_TYPE_INCLUDES:
    case EXPRESSION_TYPE_WITHIN:
    case EXPRESSION_TYPE_SUBSET:
      return rightSide;

    default:
      throw new Error("Unexpected operator \"" + operator + "\"");
  }
};

var wrapValue = function wrapValue(operator, type, value) {
  switch (operator) {
    case EXPRESSION_TYPE_EQUALS:
    case EXPRESSION_TYPE_GREATER_THAN:
    case EXPRESSION_TYPE_GREATER_THAN_EQUALS:
    case EXPRESSION_TYPE_LESS_THAN:
    case EXPRESSION_TYPE_LESS_THAN_EQUALS:
      switch (type) {
        case VALUE_TYPE_TEXT:
          return {
            _type: type,
            value: value
          };

        case VALUE_TYPE_TEXT_LIST:
          return {
            _type: type,
            values: value
          };

        case VALUE_TYPE_NUMBER:
          return {
            _type: type,
            value: value
          };

        case VALUE_TYPE_VERSION:
          return {
            _type: type,
            value: value
          };

        default:
          throw new Error("Unexpected rightSide type \"" + type + "\"");
      }

    case EXPRESSION_TYPE_INCLUDES:
    case EXPRESSION_TYPE_WITHIN:
    case EXPRESSION_TYPE_SUBSET:
      return value;

    default:
      throw new Error("Unexpected operator \"" + operator + "\"");
  }
};

var convertText2TextList = function convertText2TextList(value) {
  return String(value).trim() === '' ? [] : String(value).split(/,\s*/);
};

var convertText2Number = function convertText2Number(value) {
  if (String(value).trim() === '') return '';
  var n = Number(value);
  return Number.isInteger(n) ? n : '';
};

var convertTextList2Text = function convertTextList2Text(value, param) {
  return joinStrings(value);
};

var convertTextList2Number = function convertTextList2Number(value) {
  return value.length === 1 ? convertText2Number(value[0]) : '';
};

var convertNumber2Text = function convertNumber2Text(value) {
  return String(value);
};

var convertNumber2TextList = function convertNumber2TextList(value) {
  return [String(value)];
};

var identity = function identity(value) {
  return value;
};

var CONVERSION_TABLE = new Map([['Text:Text', identity], ['Text:TextList', convertText2TextList], ['Text:Number', convertText2Number], ['Text:Version', identity], ['TextList:Text', convertTextList2Text], ['TextList:TextList', identity], ['TextList:Number', convertTextList2Number], ['TextList:Version', convertTextList2Text], ['Number:Text', convertNumber2Text], ['Number:TextList', convertNumber2TextList], ['Number:Number', identity], ['Number:Version', convertNumber2Text], ['Version:Text', identity], ['Version:TextList', convertText2TextList], ['Version:Number', convertText2Number], ['Version:Version', identity]]);

var convertRightSide = function convertRightSide(rightSide, fromOperator, toOperator, param) {
  var fromType = getValueType(fromOperator, param);
  var toType = getValueType(toOperator, param);
  if (fromOperator === toOperator) return rightSide;
  var key = fromType + ":" + toType;
  var conversionFunction = CONVERSION_TABLE.get(key);
  var value = unwrapValue(fromOperator, rightSide);
  var convertedValue = conversionFunction(value, param);
  return wrapValue(toOperator, toType, convertedValue);
};

var getValueType = function getValueType(operator, param) {
  var _param$type, _param$type2;

  switch (operator) {
    case EXPRESSION_TYPE_EQUALS:
      return (_param$type = param === null || param === void 0 ? void 0 : param.type) != null ? _param$type : VALUE_TYPE_TEXT;

    case EXPRESSION_TYPE_GREATER_THAN:
    case EXPRESSION_TYPE_GREATER_THAN_EQUALS:
    case EXPRESSION_TYPE_LESS_THAN:
    case EXPRESSION_TYPE_LESS_THAN_EQUALS:
      return (_param$type2 = param === null || param === void 0 ? void 0 : param.type) != null ? _param$type2 : VALUE_TYPE_NUMBER;

    case EXPRESSION_TYPE_INCLUDES:
      return VALUE_TYPE_TEXT;

    case EXPRESSION_TYPE_WITHIN:
    case EXPRESSION_TYPE_SUBSET:
      return VALUE_TYPE_TEXT_LIST;

    default:
      throw new Error("Unexpected operator \"" + operator + "\"");
  }
};

var isConditionComplete = function isConditionComplete(condition) {
  var param = condition.leftSide.param;
  var isIncludes$1 = isIncludes(condition);
  var isEqualsText = isEquals(condition) && isTextValue(condition.rightSide);
  var isEqualsTextList = isEquals(condition) && isTextListValue(condition.rightSide);

  if (isIncludes$1 || isEqualsText || isEqualsTextList) {
    return Boolean(param);
  }

  var value = unwrapValueAsString(condition);
  return Boolean(param) && Boolean(value);
};

var isExpressionComplete = function isExpressionComplete(treeForm) {
  var checkExpression = function checkExpression(e) {
    if (!e) return false;

    switch (e._type) {
      case EXPRESSION_TYPE_AND:
      case EXPRESSION_TYPE_OR:
        return checkExpression(e.leftExpr) && checkExpression(e.rightExpr);

      case EXPRESSION_TYPE_NOT:
        return checkExpression(e.expr);

      case EXPRESSION_TYPE_EQUALS:
      case EXPRESSION_TYPE_GREATER_THAN:
      case EXPRESSION_TYPE_GREATER_THAN_EQUALS:
      case EXPRESSION_TYPE_LESS_THAN:
      case EXPRESSION_TYPE_LESS_THAN_EQUALS:
      case EXPRESSION_TYPE_INCLUDES:
      case EXPRESSION_TYPE_WITHIN:
      case EXPRESSION_TYPE_SUBSET:
        return isConditionComplete(e);

      default:
        throw new Error("Unexpected expression \"" + e._type + "\"");
    }
  };

  try {
    return checkExpression(treeForm);
  } catch (_unused) {
    return false;
  }
};

var Param = function Param(_ref) {
  var condition = _ref.condition,
      updateListForm = _ref.updateListForm,
      path = _ref.path;
  var params = useContext(ParamsContext);
  var options = params.map(function (param) {
    return {
      label: param.label || param.name,
      value: param.name
    };
  });

  var onChangeParam = function onChangeParam(selectedValue) {
    var _param$type;

    var param = params.find(function (_ref2) {
      var name = _ref2.name;
      return name === selectedValue;
    });
    var type = (_param$type = param === null || param === void 0 ? void 0 : param.type) != null ? _param$type : VALUE_TYPE_TEXT;
    var rightSide = type === VALUE_TYPE_TEXT_LIST ? {
      _type: type,
      values: []
    } : {
      _type: type,
      value: ''
    };
    var newCondition = {
      _type: EXPRESSION_TYPE_EQUALS,
      leftSide: {
        param: selectedValue
      },
      rightSide: rightSide
    };
    updateListForm(path, newCondition);
  };

  return /*#__PURE__*/React.createElement(NativeSelect, {
    style: {
      width: '150px'
    },
    placeholder: condition.leftSide.param ? '' : 'Choose...',
    options: options,
    value: condition.leftSide.param,
    onChange: onChangeParam
  });
};

var makeNotValue = function makeNotValue(expressionType) {
  return [expressionType, EXPRESSION_TYPE_NOT].join(':');
};

var OPTIONS = [{
  label: '=',
  value: EXPRESSION_TYPE_EQUALS,
  applicableParamTypes: ['Text', 'TextList', 'Number', 'Version']
}, {
  label: '!=',
  value: makeNotValue(EXPRESSION_TYPE_EQUALS),
  applicableParamTypes: ['Text', 'TextList', 'Number', 'Version']
}, {
  label: '>',
  value: EXPRESSION_TYPE_GREATER_THAN,
  applicableParamTypes: ['Number', 'Version']
}, {
  label: '>=',
  value: EXPRESSION_TYPE_GREATER_THAN_EQUALS,
  applicableParamTypes: ['Number', 'Version']
}, {
  label: '<',
  value: EXPRESSION_TYPE_LESS_THAN,
  applicableParamTypes: ['Number', 'Version']
}, {
  label: '<=',
  value: EXPRESSION_TYPE_LESS_THAN_EQUALS,
  applicableParamTypes: ['Number', 'Version']
}, {
  label: 'includes',
  value: EXPRESSION_TYPE_INCLUDES,
  applicableParamTypes: ['TextList']
}, {
  label: 'not includes',
  value: makeNotValue(EXPRESSION_TYPE_INCLUDES),
  applicableParamTypes: ['TextList']
}, {
  label: 'within',
  value: EXPRESSION_TYPE_WITHIN,
  applicableParamTypes: ['Text']
}, {
  label: 'not within',
  value: makeNotValue(EXPRESSION_TYPE_WITHIN),
  applicableParamTypes: ['Text']
}, {
  label: 'subset',
  value: EXPRESSION_TYPE_SUBSET,
  applicableParamTypes: ['TextList']
}, {
  label: 'not subset',
  value: makeNotValue(EXPRESSION_TYPE_SUBSET),
  applicableParamTypes: ['TextList']
}];

var Operator = function Operator(_ref) {
  var condition = _ref.condition,
      updateListForm = _ref.updateListForm,
      path = _ref.path,
      param = _ref.param,
      _ref$predicateMode = _ref.predicateMode,
      predicateMode = _ref$predicateMode === void 0 ? false : _ref$predicateMode;
  var byParamType = param ? function (option) {
    var _param$type;

    return option.applicableParamTypes.includes((_param$type = param.type) != null ? _param$type : VALUE_TYPE_TEXT);
  } : function (_option) {
    return true;
  };
  var byMode = predicateMode ? function (option) {
    return option.value.startsWith(EXPRESSION_TYPE_EQUALS);
  } : function (_option) {
    return true;
  };
  var options = OPTIONS.filter(byParamType).filter(byMode);

  var onChangeOperator = function onChangeOperator(selectedValue) {
    var fromOperator = condition._type;

    var _selectedValue$split = selectedValue.split(':'),
        toOperator = _selectedValue$split[0],
        notSuffix = _selectedValue$split[1];

    var convertedRightSide = convertRightSide(condition.rightSide, fromOperator, toOperator, param);
    var maybeNot = notSuffix === EXPRESSION_TYPE_NOT ? {
      not: true
    } : undefined;

    var newCondition = _extends({
      _type: toOperator,
      leftSide: condition.leftSide,
      rightSide: convertedRightSide
    }, maybeNot);

    updateListForm(path, newCondition);
  };

  return /*#__PURE__*/React.createElement(NativeSelect, {
    style: {
      width: '125px'
    },
    options: options,
    value: condition.not ? makeNotValue(condition._type) : condition._type,
    onChange: onChangeOperator,
    disabled: !param
  });
};

var isBlank = function isBlank(value) {
  return value === '';
};

var isStringyBlank = function isStringyBlank(value) {
  return String(value).trim() === '';
};

var isDuplicate = function isDuplicate(value, index, values) {
  return values.findIndex(function (otherValue, otherIndex) {
    return otherValue === value && otherIndex !== index;
  }) >= 0;
};

var isStringyDuplicate = function isStringyDuplicate(value, index, values) {
  return values.findIndex(function (otherValue, otherIndex) {
    return String(otherValue).trim() === String(value).trim() && otherIndex !== index;
  }) >= 0;
};

var isNotInteger = function isNotInteger(value) {
  return !Number.isInteger(value);
};

var makeErrorMessageMap = function makeErrorMessageMap(values, type) {
  return new Map(values.flatMap(function (value, index) {
    if (isBlank(value) || isStringyBlank(value)) {
      return Array.of([index, 'Blank values not allowed']);
    }

    if (isDuplicate(value, index, values) || isStringyDuplicate(value, index, values)) {
      return Array.of([index, 'Duplicate values not allowed']);
    }

    if (type === 'Number' && isNotInteger(value)) {
      return Array.of([index, 'Value must be an integer']);
    }

    return [];
  }));
};

var makeOptionsForParamValues = function makeOptionsForParamValues(param) {
  if ((param === null || param === void 0 ? void 0 : param.type) === VALUE_TYPE_TEXT_LIST) {
    return [];
  }

  var paramValues = param && Array.isArray(param.values) ? param.values : [];
  var options = paramValues.map(function (paramValue) {
    var paramValueAsString = String(paramValue);
    return {
      label: paramValueAsString,
      value: paramValueAsString,
      originalParamValue: paramValue
    };
  });
  return options;
};

var styles$1 = {
  "values": "_3FzKl",
  "value": "_17HwR",
  "field": "_3l-Yb",
  "actions": "_3EwzH",
  "delete-icon": "_HqPfI"
};

var TextListModal = function TextListModal(_ref) {
  var condition = _ref.condition,
      show = _ref.show,
      onHide = _ref.onHide,
      onOK = _ref.onOK;
  var paramName = condition.leftSide.param;
  var operator = condition._type;
  var not = condition.not;
  var subtitle = paramName + " " + (not ? 'not' : '') + " " + operator.toLowerCase();
  var params = useContext(ParamsContext);
  var param = params.find(function (param) {
    return param.name === paramName;
  });
  var options = makeOptionsForParamValues(param);

  var _useState = useState(function () {
    return unwrapTextList(condition);
  }),
      currentValues = _useState[0],
      setCurrentValues = _useState[1];

  var onAddValue = function onAddValue() {
    setCurrentValues(function (currentValues) {
      return [].concat(currentValues, ['']);
    });
  };

  var onDeleteValue = function onDeleteValue(indexToDelete) {
    setCurrentValues(function (currentValues) {
      return currentValues.filter(function (_, index) {
        return index !== indexToDelete;
      });
    });
  };

  var onChangeValue = function onChangeValue(newValue, indexToUpdate) {
    setCurrentValues(function (currentValues) {
      return currentValues.map(function (existingValue, index) {
        return index === indexToUpdate ? newValue : existingValue;
      });
    });
  };

  var valuesErrorMessageMap = makeErrorMessageMap(currentValues, 'Text');
  var cantSave = valuesErrorMessageMap.size > 0;
  return /*#__PURE__*/React.createElement(Modal, {
    isVisible: show,
    close: onHide,
    centered: true,
    animation: false
  }, /*#__PURE__*/React.createElement(ModalTitle, null, "Edit TextList Values"), /*#__PURE__*/React.createElement(ModalSubtitle, null, subtitle), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement("div", {
    className: styles$1.values
  }, currentValues.map(function (value, index) {
    return /*#__PURE__*/React.createElement("div", {
      key: index,
      className: styles$1.value
    }, /*#__PURE__*/React.createElement("div", {
      className: styles$1.field
    }, options.length ? /*#__PURE__*/React.createElement(NativeSelect, {
      placeholder: value ? '' : 'Choose...',
      options: options,
      value: value,
      onChange: function onChange(newValue) {
        return onChangeValue(newValue, index);
      },
      errorMessage: valuesErrorMessageMap.get(index)
    }) : /*#__PURE__*/React.createElement(TextField, {
      value: value,
      onChange: function onChange(newValue) {
        return onChangeValue(newValue, index);
      },
      errorMessage: valuesErrorMessageMap.get(index)
    })), /*#__PURE__*/React.createElement("div", {
      className: styles$1['delete-icon']
    }, /*#__PURE__*/React.createElement(FaTimes, {
      onClick: function onClick() {
        return onDeleteValue(index);
      }
    })));
  })), /*#__PURE__*/React.createElement("div", {
    className: styles$1.actions
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "secondary",
    onClick: onAddValue
  }, /*#__PURE__*/React.createElement(FaPlus, null), " Value"))), /*#__PURE__*/React.createElement(ModalActionsContainer, null, /*#__PURE__*/React.createElement(Button, {
    variant: "secondary",
    onClick: onHide
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    onClick: function onClick() {
      return onOK(currentValues);
    },
    disabled: cantSave
  }, "OK")));
};

var styles$2 = {
  "value": "_3VknV",
  "value--textlist": "_3Iul9",
  "value__overlay": "_2fusX"
};

var ValueText = function ValueText(_ref) {
  var condition = _ref.condition,
      onChangeValue = _ref.onChangeValue,
      options = _ref.options;
  var value = unwrapValueAsString(condition);

  if (options.length) {
    return /*#__PURE__*/React.createElement(NativeSelect, {
      placeholder: value === '' ? 'Choose...' : '',
      options: options,
      value: value,
      onChange: onChangeValue
    });
  }

  return /*#__PURE__*/React.createElement(TextField, {
    value: value,
    onChange: onChangeValue
  });
};

var ValueTextList = function ValueTextList(_ref2) {
  var condition = _ref2.condition,
      onChangeValue = _ref2.onChangeValue;
  var textListModal = useModal();

  var onTextListModalOK = function onTextListModalOK(values) {
    onChangeValue(values);
    textListModal.close();
  };

  return /*#__PURE__*/React.createElement("div", {
    className: "" + styles$2['value--textlist']
  }, /*#__PURE__*/React.createElement(TextField, {
    value: unwrapValueAsString(condition),
    onChange: onChangeValue,
    readOnly: true
  }), /*#__PURE__*/React.createElement("div", {
    className: styles$2.value__overlay
  }, /*#__PURE__*/React.createElement(Button, {
    small: true,
    as: "button",
    onClick: textListModal.open
  }, "\u2026")), textListModal.isVisible && /*#__PURE__*/React.createElement(TextListModal, {
    condition: condition,
    show: textListModal.isVisible,
    onHide: textListModal.close,
    onOK: onTextListModalOK
  }));
};

var numberFieldKeyHack = 1;

var ValueNumber = function ValueNumber(_ref3) {
  var condition = _ref3.condition,
      onChangeValue = _ref3.onChangeValue,
      options = _ref3.options;
  var value = unwrapValueAsString(condition);

  if (options.length) {
    return /*#__PURE__*/React.createElement(NativeSelect, {
      placeholder: value === '' ? 'Choose...' : '',
      options: options,
      value: value,
      onChange: function onChange(value) {
        return onChangeValue(Number(value));
      }
    });
  }

  var key = value === '' ? ++numberFieldKeyHack : numberFieldKeyHack;
  return /*#__PURE__*/React.createElement(NumberField, {
    key: key,
    value: value,
    onChange: onChangeValue
  });
};

var ValueVersion = function ValueVersion(_ref4) {
  var condition = _ref4.condition,
      onChangeValue = _ref4.onChangeValue,
      options = _ref4.options;
  var value = unwrapValueAsString(condition);

  if (options.length) {
    return /*#__PURE__*/React.createElement(NativeSelect, {
      placeholder: value === '' ? 'Choose...' : '',
      options: options,
      value: value,
      onChange: onChangeValue
    });
  }

  return /*#__PURE__*/React.createElement(TextField, {
    value: value,
    onChange: onChangeValue
  });
};

var Value = function Value(_ref5) {
  var condition = _ref5.condition,
      updateListForm = _ref5.updateListForm,
      path = _ref5.path,
      param = _ref5.param;
  var options = makeOptionsForParamValues(param);

  var onChangeValue = function onChangeValue(value) {
    var _param$type;

    var rightSide = wrapValue(condition._type, (_param$type = param.type) != null ? _param$type : VALUE_TYPE_TEXT, value);
    updateListForm([].concat(path, ['rightSide']), rightSide);
  };

  var renderValue = function renderValue() {
    switch (getValueType(condition._type, param)) {
      case VALUE_TYPE_TEXT:
      default:
        return /*#__PURE__*/React.createElement(ValueText, {
          condition: condition,
          onChangeValue: onChangeValue,
          options: options
        });

      case VALUE_TYPE_TEXT_LIST:
        return /*#__PURE__*/React.createElement(ValueTextList, {
          condition: condition,
          onChangeValue: onChangeValue
        });

      case VALUE_TYPE_NUMBER:
        return /*#__PURE__*/React.createElement(ValueNumber, {
          condition: condition,
          onChangeValue: onChangeValue,
          options: options
        });

      case VALUE_TYPE_VERSION:
        return /*#__PURE__*/React.createElement(ValueVersion, {
          condition: condition,
          onChangeValue: onChangeValue,
          options: options
        });
    }
  };

  var renderEmptyValue = function renderEmptyValue() {
    return /*#__PURE__*/React.createElement(TextField, {
      readOnly: true
    });
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles$2.value
  }, param ? renderValue() : renderEmptyValue());
};

var styles$3 = {
  "condition": "_1ag3E",
  "delete-icon": "_2cFve"
};

var Condition = function Condition(_ref) {
  var condition = _ref.condition,
      index = _ref.index,
      updateListForm = _ref.updateListForm,
      path = _ref.path,
      onDeleteCondition = _ref.onDeleteCondition;
  var params = useContext(ParamsContext);
  var param = params.find(function (_ref2) {
    var name = _ref2.name;
    return name === condition.leftSide.param;
  });
  return /*#__PURE__*/React.createElement("div", {
    className: styles$3.condition
  }, /*#__PURE__*/React.createElement(Param, {
    condition: condition,
    updateListForm: updateListForm,
    path: path
  }), /*#__PURE__*/React.createElement(Operator, {
    condition: condition,
    updateListForm: updateListForm,
    path: path,
    param: param,
    predicateMode: true
  }), /*#__PURE__*/React.createElement(Value, {
    condition: condition,
    updateListForm: updateListForm,
    path: path,
    param: param
  }), /*#__PURE__*/React.createElement("span", {
    className: styles$3['delete-icon']
  }, /*#__PURE__*/React.createElement(FaTimes, {
    onClick: function onClick() {
      return onDeleteCondition(index);
    }
  })));
};

var styles$4 = {
  "panel": "_3hCIe",
  "controls": "_1kRfy"
};

var RuleBuilderPanel = function RuleBuilderPanel(_ref) {
  var listForm = _ref.listForm,
      updateListForm = _ref.updateListForm;

  var onAddCondition = function onAddCondition() {
    var newCondition = makeEqualsText('', '');
    updateListForm(['childNodes'], [].concat(listForm.childNodes, [newCondition]));
  };

  var onDeleteCondition = function onDeleteCondition(indexToDelete) {
    var newChildNodes = listForm.childNodes.filter(function (_, index) {
      return index !== indexToDelete;
    });

    if (newChildNodes.length > 0) {
      updateListForm(['childNodes'], newChildNodes);
    } else {
      var newCondition = makeEqualsText('', '');
      updateListForm(['childNodes'], [newCondition]);
    }
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles$4.panel
  }, listForm.childNodes.map(function (condition, index) {
    return /*#__PURE__*/React.createElement(Condition, {
      key: index,
      condition: condition,
      index: index,
      updateListForm: updateListForm,
      path: ['childNodes', index],
      onDeleteCondition: onDeleteCondition
    });
  }), /*#__PURE__*/React.createElement("div", {
    className: styles$4.controls
  }, /*#__PURE__*/React.createElement(Button, {
    small: true,
    as: "button",
    disabled: listForm.childNodes.some(function (condition) {
      return !isConditionComplete(condition);
    }),
    onClick: onAddCondition
  }, /*#__PURE__*/React.createElement(FaPlus, null), " Condition")));
};

var EMPTY_LIST_FORM = {
  _type: EXPRESSION_TYPE_AND,
  childNodes: [makeEqualsText('', '')]
};

var canEditExpression = function canEditExpression(expression) {
  try {
    var normalisedExpression = normalise(expression);
    toPredicateListForm(normalisedExpression);
    return true;
  } catch (_unused) {
    return false;
  }
};

var PredicateRuleBuilder = function PredicateRuleBuilder(_ref) {
  var expression = _ref.expression,
      params = _ref.params,
      onChange = _ref.onChange;

  var _useState = useState(function () {
    try {
      var normalisedExpression = normalise(expression);
      return toPredicateListForm(normalisedExpression);
    } catch (_unused2) {
      return EMPTY_LIST_FORM;
    }
  }),
      listForm = _useState[0],
      setListForm = _useState[1];

  var _useState2 = useState(undefined),
      treeForm = _useState2[0],
      setTreeForm = _useState2[1];

  useEffect(function () {
    try {
      setTreeForm(toTreeForm(listForm));
    } catch (_unused3) {
      setTreeForm(null);
    }
  }, [listForm]);
  useEffect(function () {
    if (!onChange) {
      return;
    }

    if (treeForm === undefined) {
      return;
    }

    onChange(treeForm);
  }, [treeForm]);
  useEffect(function () {
    try {
      if (expression) {
        var normalisedExpression = normalise(expression);

        if (!deepEquals(normalisedExpression, treeForm)) {
          setListForm(toPredicateListForm(normalisedExpression));
        }
      } else {
        setListForm(EMPTY_LIST_FORM);
      }
    } catch (_unused4) {}
  }, [expression]);

  var updateListForm = function updateListForm(path, newValue) {
    var lens = lensPath(path);
    var newListForm = set(lens, newValue, listForm);
    setListForm(newListForm);
  };

  var textParams = params.filter(function (param) {
    return param.type === VALUE_TYPE_TEXT;
  });
  return /*#__PURE__*/React.createElement(ParamsContext.Provider, {
    value: textParams
  }, /*#__PURE__*/React.createElement(RuleBuilderPanel, {
    listForm: listForm,
    updateListForm: updateListForm
  }), /*#__PURE__*/React.createElement(PrettyPrintPanel, {
    treeForm: treeForm
  }));
};

PredicateRuleBuilder.canEditExpression = canEditExpression;

var validateBasicListForm = function validateBasicListForm(listForm) {
  var level0Node = listForm;
  if (!isAnd(level0Node)) return false;
  if (level0Node.not) return false;
  var level1Nodes = level0Node.childNodes;
  if (level1Nodes.some(function (node) {
    return !isOr(node);
  })) return false;
  if (level1Nodes.some(function (node) {
    return node.not;
  })) return false;
  var level2Nodes = level1Nodes.flatMap(function (node) {
    return node.childNodes;
  });
  if (level2Nodes.some(isNestedExpression)) return false;
  return true;
};

var wrapInAndNode$1 = function wrapInAndNode(node) {
  return {
    _type: EXPRESSION_TYPE_AND,
    childNodes: [node]
  };
};

var wrapInOrNode = function wrapInOrNode(node) {
  return {
    _type: EXPRESSION_TYPE_OR,
    childNodes: [node]
  };
};

var toBasicListForm = function toBasicListForm(expression) {
  var listForm = toListForm(expression);

  if (!isAnd(listForm)) {
    listForm = wrapInAndNode$1(listForm);
  }

  listForm.childNodes.forEach(function (node, index) {
    if (!isOr(node)) {
      listForm.childNodes[index] = wrapInOrNode(node);
    }
  });

  if (!validateBasicListForm(listForm)) {
    throw new Error('Expression cannot be represented in basic list form');
  }

  return listForm;
};

var styles$5 = {
  "condition": "_1UfBu",
  "delete-icon": "_3OnkW"
};

var Condition$1 = function Condition(_ref) {
  var condition = _ref.condition,
      index = _ref.index,
      isLast = _ref.isLast,
      updateListForm = _ref.updateListForm,
      path = _ref.path,
      onAddCondition = _ref.onAddCondition,
      onDeleteCondition = _ref.onDeleteCondition;
  var params = useContext(ParamsContext);
  var param = params.find(function (_ref2) {
    var name = _ref2.name;
    return name === condition.leftSide.param;
  });
  return /*#__PURE__*/React.createElement("div", {
    className: styles$5.condition
  }, /*#__PURE__*/React.createElement(Param, {
    condition: condition,
    updateListForm: updateListForm,
    path: path
  }), /*#__PURE__*/React.createElement(Operator, {
    condition: condition,
    updateListForm: updateListForm,
    path: path,
    param: param
  }), /*#__PURE__*/React.createElement(Value, {
    condition: condition,
    updateListForm: updateListForm,
    path: path,
    param: param
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Button, {
    small: true,
    as: "button",
    disabled: !isLast || !isConditionComplete(condition),
    onClick: onAddCondition
  }, "OR")), /*#__PURE__*/React.createElement("span", {
    className: styles$5['delete-icon']
  }, /*#__PURE__*/React.createElement(FaTimes, {
    onClick: function onClick() {
      return onDeleteCondition(index);
    }
  })));
};

var styles$6 = {
  "group": "_3eeYu",
  "group--not-first": "_jJ8hJ",
  "and": "_3F6GR"
};

var Group = function Group(_ref) {
  var group = _ref.group,
      groupIndex = _ref.groupIndex,
      updateListForm = _ref.updateListForm,
      path = _ref.path,
      onDeleteGroup = _ref.onDeleteGroup;

  var onAddCondition = function onAddCondition() {
    var newCondition = makeEqualsText('', '');
    updateListForm([].concat(path, ['childNodes']), [].concat(group.childNodes, [newCondition]));
  };

  var onDeleteCondition = function onDeleteCondition(indexToDelete) {
    var newChildNodes = group.childNodes.filter(function (_, index) {
      return index !== indexToDelete;
    });

    if (newChildNodes.length > 0) {
      updateListForm([].concat(path, ['childNodes']), newChildNodes);
    } else {
      onDeleteGroup(groupIndex);
    }
  };

  var isFirst = groupIndex === 0;
  return /*#__PURE__*/React.createElement("div", {
    className: styles$6.group + " " + (!isFirst ? styles$6['group--not-first'] : '')
  }, !isFirst && /*#__PURE__*/React.createElement("div", {
    className: styles$6.and
  }, "And"), group.childNodes.map(function (condition, index) {
    return /*#__PURE__*/React.createElement(Condition$1, {
      key: index,
      condition: condition,
      index: index,
      isLast: index === group.childNodes.length - 1,
      updateListForm: updateListForm,
      path: [].concat(path, ['childNodes', index]),
      onAddCondition: onAddCondition,
      onDeleteCondition: onDeleteCondition
    });
  }));
};

var styles$7 = {
  "panel": "_1a_uP",
  "controls": "_36sfQ"
};

var RuleBuilderPanel$1 = function RuleBuilderPanel(_ref) {
  var listForm = _ref.listForm,
      updateListForm = _ref.updateListForm;

  var onAddGroup = function onAddGroup() {
    var newCondition = makeEqualsText('', '');
    var newGroup = {
      _type: 'Or',
      childNodes: [newCondition]
    };
    updateListForm(['childNodes'], [].concat(listForm.childNodes, [newGroup]));
  };

  var onDeleteGroup = function onDeleteGroup(indexToDelete) {
    var newChildNodes = listForm.childNodes.filter(function (_, index) {
      return index !== indexToDelete;
    });

    if (newChildNodes.length > 0) {
      updateListForm(['childNodes'], newChildNodes);
    } else {
      var newCondition = makeEqualsText('', '');
      var newGroup = {
        _type: 'Or',
        childNodes: [newCondition]
      };
      updateListForm(['childNodes'], [newGroup]);
    }
  };

  var allConditions = listForm.childNodes.flatMap(function (_ref2) {
    var childNodes = _ref2.childNodes;
    return childNodes;
  });
  return /*#__PURE__*/React.createElement("div", {
    className: styles$7.panel
  }, listForm.childNodes.map(function (group, index) {
    return /*#__PURE__*/React.createElement(Group, {
      key: index,
      group: group,
      groupIndex: index,
      updateListForm: updateListForm,
      path: ['childNodes', index],
      onDeleteGroup: onDeleteGroup
    });
  }), /*#__PURE__*/React.createElement("div", {
    className: styles$7.controls
  }, /*#__PURE__*/React.createElement(Button, {
    small: true,
    as: "button",
    disabled: allConditions.some(function (condition) {
      return !isConditionComplete(condition);
    }),
    onClick: onAddGroup
  }, "AND")));
};

var EMPTY_LIST_FORM$1 = {
  _type: EXPRESSION_TYPE_AND,
  childNodes: [{
    _type: EXPRESSION_TYPE_OR,
    childNodes: [makeEqualsText('', '')]
  }]
};

var canEditExpression$1 = function canEditExpression(expression) {
  try {
    var normalisedExpression = normalise(expression);
    toBasicListForm(normalisedExpression);
    return true;
  } catch (_unused) {
    return false;
  }
};

var BasicRuleBuilder = function BasicRuleBuilder(_ref) {
  var expression = _ref.expression,
      params = _ref.params,
      onChange = _ref.onChange;

  var _useState = useState(function () {
    try {
      var normalisedExpression = normalise(expression);
      return toBasicListForm(normalisedExpression);
    } catch (_unused2) {
      return EMPTY_LIST_FORM$1;
    }
  }),
      listForm = _useState[0],
      setListForm = _useState[1];

  var _useState2 = useState(undefined),
      treeForm = _useState2[0],
      setTreeForm = _useState2[1];

  useEffect(function () {
    try {
      setTreeForm(toTreeForm(listForm));
    } catch (_unused3) {
      setTreeForm(null);
    }
  }, [listForm]);
  useEffect(function () {
    if (!onChange) {
      return;
    }

    if (treeForm === undefined) {
      return;
    }

    onChange(treeForm);
  }, [treeForm]);
  useEffect(function () {
    try {
      if (expression) {
        var normalisedExpression = normalise(expression);

        if (!deepEquals(normalisedExpression, treeForm)) {
          setListForm(toBasicListForm(normalisedExpression));
        }
      } else {
        setListForm(EMPTY_LIST_FORM$1);
      }
    } catch (_unused4) {}
  }, [expression]);

  var updateListForm = function updateListForm(path, newValue) {
    var lens = lensPath(path);
    var newListForm = set(lens, newValue, listForm);
    setListForm(newListForm);
  };

  return /*#__PURE__*/React.createElement(ParamsContext.Provider, {
    value: params
  }, /*#__PURE__*/React.createElement(RuleBuilderPanel$1, {
    listForm: listForm,
    updateListForm: updateListForm
  }), /*#__PURE__*/React.createElement(PrettyPrintPanel, {
    treeForm: treeForm
  }));
};

BasicRuleBuilder.canEditExpression = canEditExpression$1;

var wrapInAndNode$2 = function wrapInAndNode(node) {
  return {
    _type: EXPRESSION_TYPE_AND,
    childNodes: [node]
  };
};

var toAdvancedListForm = function toAdvancedListForm(e) {
  var e2 = toListForm(e);
  return isNestedExpression(e2) ? e2 : wrapInAndNode$2(e2);
};

var styles$8 = {
  "mode": "_1tsAL",
  "alignment-hack": "_3BkI6"
};

var Mode = function Mode(_ref) {
  var group = _ref.group,
      updateListForm = _ref.updateListForm,
      path = _ref.path;

  var onChangeType = function onChangeType(value) {
    updateListForm([].concat(path, ['_type']), value);
  };

  var onChangeNot = function onChangeNot(value) {
    return updateListForm([].concat(path, ['not']), value);
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles$8.mode
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$8['alignment-hack']
  }, /*#__PURE__*/React.createElement(RadioGroup, {
    name: [].concat(path, ['type']).join('-'),
    type: "button-group",
    list: [{
      label: 'And',
      value: EXPRESSION_TYPE_AND
    }, {
      label: 'Or',
      value: EXPRESSION_TYPE_OR
    }],
    selectedValue: group._type,
    onChange: onChangeType
  })), /*#__PURE__*/React.createElement(ToggleSwitch, {
    label: "Not",
    checked: Boolean(group.not),
    onChange: onChangeNot
  }));
};

var styles$9 = {
  "condition": "_-2CKz",
  "delete-icon": "_2d2U-"
};

var Condition$2 = function Condition(_ref) {
  var condition = _ref.condition,
      index = _ref.index,
      updateListForm = _ref.updateListForm,
      path = _ref.path,
      onDeleteCondition = _ref.onDeleteCondition;
  var params = useContext(ParamsContext);
  var param = params.find(function (_ref2) {
    var name = _ref2.name;
    return name === condition.leftSide.param;
  });
  return /*#__PURE__*/React.createElement("div", {
    className: styles$9.condition
  }, /*#__PURE__*/React.createElement(Param, {
    condition: condition,
    updateListForm: updateListForm,
    path: path
  }), /*#__PURE__*/React.createElement(Operator, {
    condition: condition,
    updateListForm: updateListForm,
    path: path,
    param: param
  }), /*#__PURE__*/React.createElement(Value, {
    condition: condition,
    updateListForm: updateListForm,
    path: path,
    param: param
  }), /*#__PURE__*/React.createElement("span", {
    className: styles$9['delete-icon']
  }, /*#__PURE__*/React.createElement(FaTimes, {
    onClick: function onClick() {
      return onDeleteCondition(index);
    }
  })));
};

var styles$a = {
  "actions": "_3aXgi"
};

var Actions = function Actions(_ref) {
  var group = _ref.group,
      updateListForm = _ref.updateListForm,
      path = _ref.path,
      level = _ref.level;

  var onAddCondition = function onAddCondition() {
    var newCondition = makeEqualsText('', '');
    updateListForm([].concat(path, ['childNodes']), [].concat(group.childNodes, [newCondition]));
  };

  var onAddGroup = function onAddGroup() {
    var newGroup = {
      _type: EXPRESSION_TYPE_AND,
      childNodes: [makeEqualsText('', '')]
    };
    updateListForm([].concat(path, ['childNodes']), [].concat(group.childNodes, [newGroup]));
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles$a.actions
  }, /*#__PURE__*/React.createElement(Button, {
    small: true,
    variant: "secondary",
    className: "mr-2",
    onClick: onAddCondition
  }, /*#__PURE__*/React.createElement(FaPlus, null), " Condition"), level < 2 && /*#__PURE__*/React.createElement(Button, {
    small: true,
    variant: "secondary",
    onClick: onAddGroup
  }, /*#__PURE__*/React.createElement(FaPlus, null), " Group"));
};

var styles$b = {
  "group": "_3MtXF",
  "delete-icon": "_3R7Lx"
};

var Group$1 = function Group(_ref) {
  var group = _ref.group,
      index = _ref.index,
      updateListForm = _ref.updateListForm,
      path = _ref.path,
      onDeleteGroup = _ref.onDeleteGroup,
      _ref$level = _ref.level,
      level = _ref$level === void 0 ? 0 : _ref$level;

  var onDelete = function onDelete(indexToDelete) {
    var newChildNodes = group.childNodes.filter(function (_, index) {
      return index !== indexToDelete;
    });
    updateListForm([].concat(path, ['childNodes']), newChildNodes);
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles$b.group
  }, /*#__PURE__*/React.createElement(Mode, {
    group: group,
    updateListForm: updateListForm,
    path: path
  }), group.childNodes.map(function (childNode, index) {
    return isNestedExpression(childNode) ? /*#__PURE__*/React.createElement(Group, {
      key: index,
      index: index,
      group: childNode,
      updateListForm: updateListForm,
      level: level + 1,
      path: [].concat(path, ['childNodes', index]),
      onDeleteGroup: onDelete
    }) : /*#__PURE__*/React.createElement(Condition$2, {
      key: index,
      index: index,
      condition: childNode,
      group: group,
      updateListForm: updateListForm,
      path: [].concat(path, ['childNodes', index]),
      onDeleteCondition: onDelete
    });
  }), /*#__PURE__*/React.createElement(Actions, {
    group: group,
    updateListForm: updateListForm,
    path: path,
    level: level
  }), level > 0 && /*#__PURE__*/React.createElement("span", {
    className: styles$b['delete-icon']
  }, /*#__PURE__*/React.createElement(FaTimes, {
    onClick: function onClick() {
      return onDeleteGroup(index);
    }
  })));
};

var styles$c = {
  "panel": "_2sLkt"
};

var RuleBuilderPanel$2 = function RuleBuilderPanel(_ref) {
  var listForm = _ref.listForm,
      updateListForm = _ref.updateListForm;
  return /*#__PURE__*/React.createElement("div", {
    className: styles$c.panel
  }, /*#__PURE__*/React.createElement(Group$1, {
    group: listForm,
    updateListForm: updateListForm,
    path: []
  }));
};

var EMPTY_LIST_FORM$2 = {
  _type: EXPRESSION_TYPE_AND,
  childNodes: [makeEqualsText('', '')]
};

var canEditExpression$2 = function canEditExpression(_expression) {
  return true;
};

var AdvancedRuleBuilder = function AdvancedRuleBuilder(_ref) {
  var expression = _ref.expression,
      params = _ref.params,
      onChange = _ref.onChange;

  var _useState = useState(function () {
    try {
      var normalisedExpression = normalise(expression);
      return toAdvancedListForm(normalisedExpression);
    } catch (_unused) {
      return EMPTY_LIST_FORM$2;
    }
  }),
      listForm = _useState[0],
      setListForm = _useState[1];

  var _useState2 = useState(undefined),
      treeForm = _useState2[0],
      setTreeForm = _useState2[1];

  useEffect(function () {
    try {
      setTreeForm(toTreeForm(listForm));
    } catch (_unused2) {
      setTreeForm(null);
    }
  }, [listForm]);
  useEffect(function () {
    if (!onChange) {
      return;
    }

    if (treeForm === undefined) {
      return;
    }

    onChange(treeForm);
  }, [treeForm]);
  useEffect(function () {
    try {
      if (expression) {
        var normalisedExpression = normalise(expression);

        if (!deepEquals(normalisedExpression, treeForm)) {
          setListForm(toAdvancedListForm(normalisedExpression));
        }
      } else {
        setListForm(EMPTY_LIST_FORM$2);
      }
    } catch (_unused3) {}
  }, [expression]);

  var updateListForm = function updateListForm(path, newValue) {
    var lens = lensPath(path);
    var newListForm = set(lens, newValue, listForm);
    setListForm(newListForm);
  };

  return /*#__PURE__*/React.createElement(ParamsContext.Provider, {
    value: params
  }, /*#__PURE__*/React.createElement(RuleBuilderPanel$2, {
    listForm: listForm,
    updateListForm: updateListForm
  }), /*#__PURE__*/React.createElement(PrettyPrintPanel, {
    treeForm: treeForm
  }));
};

AdvancedRuleBuilder.canEditExpression = canEditExpression$2;
export { AdvancedRuleBuilder, BasicRuleBuilder, expressionTreeUtils as E, PredicateRuleBuilder, deepEquals, isExpressionComplete, normalise, prettyPrint, reassembleLeftTree };